import { DomesticConstants } from "@assets/constants";
import { CommonTypes, DomesticTypes } from "@assets/types";
import i18next from "i18next";

// ---- PAYER ---------

/**
 * Returns the term type of the booking based on the selected pickup/delivery mode.
 * @param pickUpMethod The pickup mode of the booking.
 * @param dropOffMethod The delivery mode of the booking.
 * @returns Whether the booking is a H2H, H2D, D2H, D2D
 */
export function getPayerTermTypeofBooking(
  pickUpMethod: DomesticConstants.DELIVERY_MODE,
  dropOffMethod: DomesticConstants.DELIVERY_MODE
) {
  if (
    pickUpMethod === DomesticConstants.DELIVERY_MODE.DEPOT &&
    dropOffMethod === DomesticConstants.DELIVERY_MODE.DEPOT
  ) {
    return DomesticConstants.PAYER_TERM_TYPE.H2H;
  }

  if (
    pickUpMethod === DomesticConstants.DELIVERY_MODE.DEPOT &&
    dropOffMethod === DomesticConstants.DELIVERY_MODE.ADDRESS
  ) {
    return DomesticConstants.PAYER_TERM_TYPE.H2D;
  }

  if (
    pickUpMethod === DomesticConstants.DELIVERY_MODE.ADDRESS &&
    dropOffMethod === DomesticConstants.DELIVERY_MODE.DEPOT
  ) {
    return DomesticConstants.PAYER_TERM_TYPE.D2H;
  }

  if (
    pickUpMethod === DomesticConstants.DELIVERY_MODE.ADDRESS &&
    dropOffMethod === DomesticConstants.DELIVERY_MODE.ADDRESS
  ) {
    return DomesticConstants.PAYER_TERM_TYPE.D2D;
  }

  // The cases above should cover all possible cases, but in case something changes, just return H2H
  return DomesticConstants.PAYER_TERM_TYPE.H2H;
}

/**
 * Get the array of legs (with title, payer leg type, set payer) for the UI
 * @param pickUpM The pickup mode of the booking.
 * @param dropOffM The delivery mode of the booking.
 * @param addresses The From Postcode, To Postcode
 * @returns The payer legs array constructed for the display.
 */
export const generatePayerLegs = (
  pickUpM: DomesticConstants.DELIVERY_MODE,
  dropOffM: DomesticConstants.DELIVERY_MODE,
  addresses: DomesticTypes.DomesticPayerLegAddresses
): DomesticTypes.DomesticPayerLeg[] => {
  const getHubToHubLeg = (
    payer?: DomesticConstants.BOOKING_PARTY
  ): DomesticTypes.DomesticPayerLeg => {
    return {
      payer: payer,
      title: i18next.t("LABEL_M_TO_N", {
        m: `${addresses.FromAddress?.PostCode} ${addresses.FromAddress?.City}`,
        n: `${addresses.ToAddress?.PostCode} ${addresses.ToAddress?.City}`,
      }),
      type: DomesticConstants.PAYER_TERM_TYPE.H2H,
    };
  };

  const getVanReceiptLeg = (): DomesticTypes.DomesticPayerLeg => {
    return {
      payer: DomesticConstants.BOOKING_PARTY.SENDER,
      title: i18next.t("LABEL_VAN_PICK_UP_N", {
        n: `${addresses.FromAddress?.PostCode} ${addresses.FromAddress?.City}`,
      }),
      type: DomesticConstants.PAYER_TERM_TYPE.D2H,
    };
  };

  const getVanDeliveryLeg = (): DomesticTypes.DomesticPayerLeg => {
    return {
      payer: DomesticConstants.BOOKING_PARTY.RECEIVER,
      title: i18next.t("LABEL_VAN_DELIVERY_TO_N", {
        n: `${addresses.ToAddress?.PostCode} ${addresses.ToAddress?.City}`,
      }),
      type: DomesticConstants.PAYER_TERM_TYPE.H2D,
    };
  };

  const deliveryType = getPayerTermTypeofBooking(pickUpM, dropOffM);

  switch (deliveryType) {
    case DomesticConstants.PAYER_TERM_TYPE.H2H:
      return [];
    case DomesticConstants.PAYER_TERM_TYPE.H2D:
      return [
        getHubToHubLeg(DomesticConstants.BOOKING_PARTY.SENDER),
        getVanDeliveryLeg(),
      ];
    case DomesticConstants.PAYER_TERM_TYPE.D2H:
      return [
        getVanReceiptLeg(),
        getHubToHubLeg(DomesticConstants.BOOKING_PARTY.RECEIVER),
      ];
    case DomesticConstants.PAYER_TERM_TYPE.D2D:
      return [getVanReceiptLeg(), getHubToHubLeg(), getVanDeliveryLeg()];
  }
};

/**
 * Get the payer terms of the booking for the sender and receiver.
 * @param pickUpMethod The pickup mode of the booking.
 * @param dropOffMethod The delivery mode of the booking.
 * @param isSeparatePayerLegs True, if the price is split between the parties, false if the booking is paid by a single party
 * @param payer The payer of the whole booking, or in case the D2D booking is paid separately, the party who pays for the H2H leg.
 * @returns An object with the senderTerms and receiver terms
 */
export function getSenderReceiverTerms(
  pickupMethod: DomesticConstants.DELIVERY_MODE,
  dropOffMethod: DomesticConstants.DELIVERY_MODE,
  isSeparatePayerLegs: boolean,
  payer: CommonTypes.Nullable<DomesticConstants.BOOKING_PARTY>
) {
  let senderTerms: CommonTypes.Nullable<DomesticConstants.PAYER_TERM_TYPE> =
    null;
  let receiverTerms: CommonTypes.Nullable<DomesticConstants.PAYER_TERM_TYPE> =
    null;
  const bookingDeliveryType = getPayerTermTypeofBooking(
    pickupMethod,
    dropOffMethod
  );
  // If the booking is paid by a single party and the booking isn't H2H, the selected payer is assigned the booking term type
  if (
    !isSeparatePayerLegs &&
    bookingDeliveryType !== DomesticConstants.PAYER_TERM_TYPE.H2H
  ) {
    if (payer === DomesticConstants.BOOKING_PARTY.SENDER) {
      senderTerms = bookingDeliveryType;
    } else {
      receiverTerms = bookingDeliveryType;
    }
  } else {
    switch (bookingDeliveryType) {
      case DomesticConstants.PAYER_TERM_TYPE.H2D: {
        senderTerms = DomesticConstants.PAYER_TERM_TYPE.H2H;
        receiverTerms = DomesticConstants.PAYER_TERM_TYPE.H2D;
        break;
      }
      case DomesticConstants.PAYER_TERM_TYPE.D2H: {
        senderTerms = DomesticConstants.PAYER_TERM_TYPE.D2H;
        receiverTerms = DomesticConstants.PAYER_TERM_TYPE.H2H;
        break;
      }
      case DomesticConstants.PAYER_TERM_TYPE.D2D: {
        senderTerms = DomesticConstants.PAYER_TERM_TYPE.D2H;
        receiverTerms = DomesticConstants.PAYER_TERM_TYPE.H2D;
        break;
      }
      default: {
        senderTerms = null;
        receiverTerms = null;
        break;
      }
    }
  }
  return { senderTerms, receiverTerms };
}

/**
 * Return the payer of a booking
 * @param payer The selected payer for the booking, or the payer for the H2H of D2D booking.
 * @param pickupMethod The pickup mode of the booking.
 * @param dropOffMethod The delivery mode of the booking.
 * @returns The BookingParty that should be noted as the payer for the booking.
 */
export function getPayer(
  payer: CommonTypes.Nullable<DomesticConstants.BOOKING_PARTY>,
  pickupMethod: DomesticConstants.DELIVERY_MODE,
  dropOffMethod: DomesticConstants.DELIVERY_MODE
) {
  const payerTermBooking = getPayerTermTypeofBooking(
    pickupMethod,
    dropOffMethod
  );
  // If we have a payer then return the selected payer (for H2H and D2D bookings we must have a payer)
  if (payer) {
    return payer;
  }
  // If we have H2D, the payer is the sender
  if (payerTermBooking === DomesticConstants.PAYER_TERM_TYPE.H2D) {
    return DomesticConstants.BOOKING_PARTY.SENDER;
  }
  // If we have D2H, the payer is the receiver
  return DomesticConstants.BOOKING_PARTY.RECEIVER;
}
